import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Search, Filter, Clock, User, Activity } from 'lucide-react';

import { API_URL } from '../utils/apiUrl';

const AuditPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAction, setFilterAction] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/audit-logs?limit=500`);
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = !searchQuery || 
      log.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.action?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.resource?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.details?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesAction = filterAction === 'all' || log.action === filterAction;
    
    return matchesSearch && matchesAction;
  });

  const getActionBadge = (action) => {
    const styles = {
      CREATE: 'badge-success',
      UPDATE: 'badge-gold',
      DELETE: 'badge-danger',
      LOGIN: 'bg-sky-500/15 text-sky-500 border border-sky-500/30',
      REGISTER: 'bg-violet-500/15 text-violet-500 border border-violet-500/30',
      IMPORT: 'bg-emerald-500/15 text-emerald-500 border border-emerald-500/30',
      AI_QUERY: 'bg-purple-500/15 text-purple-500 border border-purple-500/30',
      AI_QUERY_BLOCKED: 'badge-danger'
    };
    
    return (
      <span className={`${styles[action] || 'bg-white/10 text-slate-300 border border-white/10'} text-xs px-2 py-1 rounded-full`}>
        {action}
      </span>
    );
  };

  const uniqueActions = [...new Set(logs.map(log => log.action))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <FileText className="w-8 h-8 text-[#C5A059]" />
          Audit Logs
        </h1>
        <p className="text-slate-400 mt-1">
          Complete audit trail of all system activities • {logs.length} records
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-vault">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Activity className="w-5 h-5 text-[#C5A059]" />
              <div>
                <p className="label-uppercase text-xs">Total Events</p>
                <p className="font-heading text-xl text-white tabular-nums">{logs.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-vault">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-sky-500" />
              <div>
                <p className="label-uppercase text-xs">Logins Today</p>
                <p className="font-heading text-xl text-white tabular-nums">
                  {logs.filter(l => l.action === 'LOGIN' && 
                    new Date(l.timestamp).toDateString() === new Date().toDateString()).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-vault">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-emerald-500" />
              <div>
                <p className="label-uppercase text-xs">AI Queries</p>
                <p className="font-heading text-xl text-white tabular-nums">
                  {logs.filter(l => l.action === 'AI_QUERY').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-vault">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Activity className="w-5 h-5 text-red-500" />
              <div>
                <p className="label-uppercase text-xs">Blocked Queries</p>
                <p className="font-heading text-xl text-white tabular-nums">
                  {logs.filter(l => l.action === 'AI_QUERY_BLOCKED').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-vault">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by user, action, resource, or details..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-vault pl-10"
                data-testid="audit-search"
              />
            </div>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="input-vault w-full md:w-48" data-testid="filter-action">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {uniqueActions.map(action => (
                  <SelectItem key={action} value={action}>{action}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="card-vault overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="table-header border-b border-white/5">
                <TableHead className="text-slate-400">Timestamp</TableHead>
                <TableHead className="text-slate-400">User</TableHead>
                <TableHead className="text-slate-400">Action</TableHead>
                <TableHead className="text-slate-400">Resource</TableHead>
                <TableHead className="text-slate-400">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No audit logs found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => (
                  <TableRow key={log.id} className="table-row" data-testid={`audit-row-${log.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2 text-slate-400">
                        <Clock className="w-3 h-3" />
                        <span className="font-mono text-xs">
                          {new Date(log.timestamp).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-white">{log.user_email}</span>
                    </TableCell>
                    <TableCell>{getActionBadge(log.action)}</TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-400 capitalize">{log.resource}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-500 max-w-xs truncate block">
                        {log.details || '-'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default AuditPage;
